import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Box,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Badge,
  HStack,
  Text,
} from "@chakra-ui/react";
import { hasDoctorRolePermissions } from "../../config/roles";
import {
  fetchChannels,
  fetchMessages,
  sendMessage,
  joinChannel,
  deleteChannel,
  markChannelRead,
  fetchPresence,
  messagingSocketUrl,
  authenticateSocket,
  getOrCreateDm,
  inviteToChannel,
  fetchTasksInvolving,
  setChannelNotify,
} from "../../services/messagingService";
import {
  useMessagingNotifications,
  refreshUnread,
  markChannelReadLocally,
  applyChannelSettingsLocally,
  setViewingChannel,
  onMessagingEvent,
} from "../../utils/messagingNotifications";
import NotificationSettings, { DesktopNotificationsPrompt } from "./NotificationSettings";
import { fetchOrders } from "../../services/clinicComplianceService";
import { onWorkItemsChanged } from "../../utils/workItemsBus";
import { uploadChatFiles } from "../../utils/chatAttachments";
import { AGENT_SENDER_ID } from "../../pages/medstudent/agents/agentsService";
import ChannelList from "./ChannelList";
import ChannelThread from "./ChannelThread";
import { mergeReadBy } from "./readReceipts";
import CreateChannelModal from "./CreateChannelModal";
import AssignModal from "./AssignModal";
import TasksPanel from "./TasksPanel";
import { countOpenForMe, countNeedsMyReview, mergeTaskSources, linkableItems } from "./tasksModel";

// How a channel's live feed answers the ways its socket closes
// (backend/api/consumers.py MessagingConsumer):
// - opened again, after a backoff: a token that was refused or has expired
//   (4401, with a refreshed token), no handshake in time (4408), an account
//   or members read that failed (4503), and the connection dropping or the
//   server going away or restarting (1006, 1001, 1011, 1012, 1013: a deploy
//   drops every socket). The delay doubles from SOCKET_RETRY_BASE_MS up to
//   SOCKET_RETRY_MAX_MS, give or take SOCKET_JITTER of it (so the Messages
//   windows open when a deploy drops them don't all reconnect in the same
//   instant), and the backoff starts over only once a socket has
//   stayed up SOCKET_STABLE_MS, so a server that accepts and closes at once
//   can't drive a fast reconnect-and-refetch loop;
// - taken out of the channel (4403): the thread is dropped and the channels
//   reloaded, as for a deleted channel;
// - anything else, or SOCKET_MAX_RETRIES failures in a row: the thread says
//   "Live updates stopped." and offers to reconnect. A feed never stops
//   without saying so.
const SOCKET_RETRY_CODES = [4401, 4408, 4503, 1001, 1006, 1011, 1012, 1013];
const SOCKET_REMOVED = 4403;
const SOCKET_RETRY_BASE_MS = 1000;
const SOCKET_RETRY_MAX_MS = 30000;
const SOCKET_STABLE_MS = 30000;
const SOCKET_MAX_RETRIES = 8;
const SOCKET_JITTER = 0.2;

// A file dropped anywhere on the Messages window that nothing takes (the
// Tasks tab, the channel list, the backdrop) does nothing, instead of the
// browser leaving the app to open it. The conversation accepts drops itself.
// Whether this tab is on screen: a channel is only marked read while someone can see it.
const tabVisible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

function preventStrayFileDrop(e) {
  if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
}

// A uuid for a post's client_key: the browser's own where it makes one.
function newClientKey() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch (err) {
    // falls through to the one made here
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r % 4) + 8).toString(16);
  });
}

export default function MessagingModal({ isOpen, onClose, currentUser, openRequest = null }) {
  const [channels, setChannels] = useState({ mine: [], joinable: [] });
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  // When the other person in the active DM last read it (null: never, or
  // not a DM). Moves forward live from the socket's "read" events.
  const [readBy, setReadBy] = useState([]);
  // Unread counts, mentions and each channel's notify settings: the app-wide store
  // (utils/messagingNotifications.js, started by MessagingHost), shared with the Navbar badge.
  const { summary } = useMessagingNotifications();
  const [presence, setPresence] = useState({});
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [channelPendingDelete, setChannelPendingDelete] = useState(null);
  const [deleteEmailConfirm, setDeleteEmailConfirm] = useState("");
  // { message?, defaultTab? } | null — from a chat message, or from the Tasks tab.
  const [assignTarget, setAssignTarget] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const tabIndexRef = useRef(tabIndex);
  tabIndexRef.current = tabIndex;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const [taskItems, setTaskItems] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskFocus, setTaskFocus] = useState(null);
  const wsRef = useRef(null);
  // "stopped" once the open thread's live feed has given up (see SOCKET_RETRY_CODES), with
  // reconnectRef.current set to start it again; "live" otherwise.
  const [liveFeed, setLiveFeed] = useState("live");
  const liveFeedRef = useRef(liveFeed);
  liveFeedRef.current = liveFeed;
  const reconnectRef = useRef(null);
  // A channel is only marked read while its messages are on screen: modal
  // open AND the Chat tab showing. Set when the active channel may hold
  // messages the user hasn't seen (it was reopened on Tasks, or one arrived
  // while Tasks was showing); it's refetched and marked read once Chat shows.
  const staleChannelRef = useRef(false);
  // The conversation on screen right now. A send (with its upload) can finish
  // after the user has switched channels; it must not land in the new thread.
  const activeChannelIdRef = useRef(null);
  activeChannelIdRef.current = activeChannel?.id ?? null;
  const toast = useToast();
  const canManageChannels = hasDoctorRolePermissions(currentUser?.role);

  // Resolves to the fresh { mine, joinable }, or null if it couldn't load.
  const reloadChannels = useCallback(async () => {
    if (!currentUser?.id) return null;
    try {
      const data = await fetchChannels(currentUser.id);
      setChannels(data);
      return data;
    } catch (err) {
      console.warn("Messaging: failed to load channels", err);
      return null;
    }
  }, [currentUser?.id]);

  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  // Tasks tab: internal_tasks involving me + Orders assigned in this clinic
  // (the panel's channel picker loads a channel's tasks itself).
  // Orders are read from the user's schema AND public — a tenant's orders
  // usually live in public.clinical_action_items. A source that fails keeps
  // its last good rows instead of blanking the list.
  const lastTaskSourcesRef = useRef({ tasks: [], orders: {} });
  useEffect(() => {
    // A different user/schema must never inherit the previous one's rows.
    lastTaskSourcesRef.current = { tasks: [], orders: {} };
  }, [currentUser?.id, currentUser?.schema]);
  const reloadTasks = useCallback(async () => {
    if (!currentUser?.id) return;
    setTasksLoading(true);
    const own = currentUser.schema || "public";
    const schemas = own === "public" ? ["public"] : [own, "public"];
    try {
      const [tasksRes, ...ordersRes] = await Promise.allSettled([
        fetchTasksInvolving(currentUser.id),
        ...schemas.map((schema) => fetchOrders({ schema })),
      ]);
      if (tasksRes.status === "rejected") console.warn("Messaging: failed to load tasks", tasksRes.reason);
      ordersRes.forEach((res, i) => {
        if (res.status === "rejected") console.warn(`Messaging: failed to load orders (${schemas[i]})`, res.reason);
      });
      const { sources, items } = mergeTaskSources(lastTaskSourcesRef.current, schemas, tasksRes, ordersRes);
      lastTaskSourcesRef.current = sources;
      setTaskItems(items);
    } finally {
      setTasksLoading(false);
    }
  }, [currentUser?.id, currentUser?.schema]);

  // The channel list the window loads as it opens, while it is on its way: an
  // agent's request that opens the window picks its thread from this one,
  // instead of loading the list a second time.
  const openLoadRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return;
    // The channel left selected last time may have been deleted, or I may
    // have been removed from it, while the modal was closed: drop it — unless
    // another channel has been picked since this reload started.
    const leftSelectedId = activeChannelIdRef.current;
    const loading = reloadChannels();
    openLoadRef.current = loading;
    loading.then((data) => {
      if (openLoadRef.current === loading) openLoadRef.current = null;
      if (!data || !leftSelectedId || activeChannelIdRef.current !== leftSelectedId) return;
      if ((data.mine || []).some((c) => c.id === leftSelectedId)) return;
      staleChannelRef.current = false;
      setActiveChannel(null);
      setMessages([]);
    });
    refreshUnread();
    reloadTasks();
  }, [isOpen, reloadChannels, reloadTasks]);

  // openMessages({tab, focus}) from anywhere (e.g. a note's task-link card):
  // each request (new nonce) picks the tab and, for Tasks, the row to focus.
  useEffect(() => {
    if (!isOpen || !openRequest) return;
    const toTasks = openRequest.tab === "tasks";
    setTabIndex(toTasks ? 1 : 0);
    setTaskFocus(toTasks ? openRequest.focus || null : null);
  }, [isOpen, openRequest]);

  // Sync: any open view (this modal, Orders & followups, Compliance) can
  // mutate a task/order — reload when that happens, and poll as a fallback
  // while the Tasks tab is actually showing.
  useEffect(() => {
    if (!isOpen) return undefined;
    return onWorkItemsChanged(() => reloadTasks());
  }, [isOpen, reloadTasks]);

  useEffect(() => {
    if (!isOpen || tabIndex !== 1) return undefined;
    const t = setInterval(reloadTasks, 30000);
    return () => clearInterval(t);
  }, [isOpen, tabIndex, reloadTasks]);

  const tasksBadgeCount = countOpenForMe(taskItems, currentUser?.id) + countNeedsMyReview(taskItems, currentUser?.id);
  const myLinkableItems = useMemo(() => linkableItems(taskItems, currentUser?.id), [taskItems, currentUser?.id]);
  const handleTaskItemChanged = (changed) =>
    setTaskItems((prev) => prev.map((item) => (item.key === changed.key ? changed : item)));

  // DM status dots: presence pings arrive every 30s, so poll on the same beat. NotesAI Agent has no
  // users row and no presence, so the DM agents posted to before they had threads of their own (kept
  // as history) is left out, and drawn without a dot. An agent's own thread is a channel, not a DM.
  const dmUserIdsKey = (channels.mine || [])
    .filter((c) => c.channel_type === "dm" && c.other_user_id && c.other_user_id !== AGENT_SENDER_ID)
    .map((c) => c.other_user_id)
    .sort()
    .join(",");
  useEffect(() => {
    if (!isOpen || !dmUserIdsKey) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { presence: next } = await fetchPresence(dmUserIdsKey.split(","));
        if (!cancelled) setPresence(next || {});
      } catch (err) {
        console.warn("Messaging: failed to load presence", err);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isOpen, dmUserIdsKey]);

  useEffect(() => {
    setDeleteEmailConfirm("");
  }, [channelPendingDelete]);

  const selectChannel = useCallback(
    async (channel) => {
      staleChannelRef.current = false;
      // On screen from now on, before the next render says so: a channel list that lands meanwhile
      // (the window's own load on opening, which drops a channel that has gone) must see it.
      activeChannelIdRef.current = channel.id;
      setActiveChannel(channel);
      activeChannelIdRef.current = channel.id; // before the render, for the check below
      setMessages([]);
      setReadBy([]);
      // Another conversation picked meanwhile: these rows (or this failure)
      // belong to one no longer on screen, which isn't marked read either.
      try {
        const { messages: rows, read_by: readers } = await fetchMessages(channel.id, currentUser.id);
        if (activeChannelIdRef.current !== channel.id) return; // switched away meanwhile
        setMessages(rows || []);
        setReadBy(readers || []);
      } catch (err) {
        if (activeChannelIdRef.current !== channel.id) return;
        toast({ title: "Couldn't load messages", description: err.message, status: "error", duration: 4000 });
      }
      try {
        await markChannelRead(channel.id, currentUser.id);
        markChannelReadLocally(channel.id);
      } catch (err) {
        console.warn("Messaging: mark-read failed", err);
      }
    },
    [currentUser?.id, toast],
  );

  // An agent's "Open in Messages" (openMessages({tab: "chat", focus: {kind:
  // "agent", id}})) opens the agent's own thread, found by its agent_id in a
  // fresh list: the agent may be newer than the list on hand (one that opens
  // the window takes the list the window is loading anyway). A newer request,
  // or the window closing, drops one still loading; so does the reader picking
  // another conversation meanwhile (theirs wins), but not one that went away.
  // A desktop notification's click ({kind: "channel", id}) opens that channel
  // the same way, found by its id.
  useEffect(() => {
    const focus = openRequest?.focus;
    const kind = focus?.kind;
    if (!isOpen || !openRequest || openRequest.tab === "tasks" || (kind !== "agent" && kind !== "channel") || !focus.id) {
      return undefined;
    }
    let dropped = false;
    const startedOn = activeChannelIdRef.current;
    (openLoadRef.current || reloadChannels()).then((data) => {
      if (dropped) return;
      if (!data) {
        toast({ title: "Couldn't load your conversations.", status: "error", duration: 4000 });
        return;
      }
      const thread = (data.mine || []).find((c) =>
        kind === "agent" ? c.agent_id && String(c.agent_id) === String(focus.id) : c.id === focus.id,
      );
      if (!thread) {
        const title = kind === "agent" ? "This agent's thread isn't in your Messages yet." : "That conversation isn't in your Messages anymore.";
        toast({ title, status: "info", duration: 4000 });
        return;
      }
      const now = activeChannelIdRef.current;
      if (thread.id === now || (now !== startedOn && now !== null)) return;
      selectChannel(thread);
    });
    return () => {
      dropped = true;
    };
  }, [isOpen, openRequest, reloadChannels, selectChannel, toast]);

  // The active channel's thread is (again) on screen: refetch its messages,
  // keeping the ones shown until they arrive, then mark it read. A failed
  // fetch marks nothing read and leaves the channel stale for the next try,
  // as does closing the modal or leaving Chat before the fetch returns.
  const refreshShownChannel = useCallback(
    async (channel) => {
      staleChannelRef.current = false;
      try {
        const { messages: rows, read_by: readers } = await fetchMessages(channel.id, currentUser.id);
        if (activeChannelIdRef.current !== channel.id) return; // switched away meanwhile
        setMessages(rows || []);
        setReadBy((prev) => (readers || []).reduce(mergeReadBy, prev));
      } catch (err) {
        staleChannelRef.current = true;
        toast({ title: "Couldn't load messages", description: err.message, status: "error", duration: 4000 });
        return;
      }
      // Not on screen (modal closed, Tasks showing, or the browser tab hidden): it
      // stays unread until it is.
      if (!isOpenRef.current || tabIndexRef.current !== 0 || !tabVisible()) {
        staleChannelRef.current = true;
        return;
      }
      try {
        await markChannelRead(channel.id, currentUser.id);
        markChannelReadLocally(channel.id);
      } catch (err) {
        console.warn("Messaging: mark-read failed", err);
      }
    },
    [currentUser?.id, toast],
  );

  // The latest refreshShownChannel, for the socket's handlers: the socket's
  // effect doesn't run again when it changes.
  const refreshShownChannelRef = useRef(refreshShownChannel);
  refreshShownChannelRef.current = refreshShownChannel;

  // The modal stays mounted across every route (MessagingHost), so a channel
  // can still be selected when it reopens. Whatever arrived while it was
  // closed is refetched and marked read only if it opens on Chat; opening on
  // Tasks leaves the channel stale until Chat is shown. On this render
  // tabIndex still holds last time's tab — the open request says which tab
  // is about to show. wasOpenRef limits this to the closed→open transition.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current && activeChannel) {
      const opensOnChat = openRequest ? openRequest.tab !== "tasks" : tabIndexRef.current === 0;
      if (opensOnChat) refreshShownChannel(activeChannel);
      else staleChannelRef.current = true;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, activeChannel, refreshShownChannel, openRequest]);

  // Switching to Chat puts the active channel's thread on screen: if it's
  // stale, refresh it and mark it read now.
  const prevTabIndexRef = useRef(tabIndex);
  useEffect(() => {
    const cameToChat = tabIndex === 0 && prevTabIndexRef.current !== 0;
    prevTabIndexRef.current = tabIndex;
    if (cameToChat && isOpen && activeChannel && staleChannelRef.current) refreshShownChannel(activeChannel);
  }, [tabIndex, isOpen, activeChannel, refreshShownChannel]);

  // Coming back to the browser tab puts the thread on screen again: what arrived
  // while it was hidden is marked read now.
  useEffect(() => {
    if (!isOpen || !activeChannel) return undefined;
    const onVisible = () => {
      if (tabVisible() && tabIndexRef.current === 0 && staleChannelRef.current) refreshShownChannelRef.current(activeChannel);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isOpen, activeChannel]);

  // What is on screen, for desktop notifications: none for the channel being read.
  const viewingId = isOpen && tabIndex === 0 && activeChannel ? activeChannel.id : null;
  useEffect(() => {
    setViewingChannel(viewingId);
  }, [viewingId]);
  useEffect(() => () => setViewingChannel(null), []);

  // The notifications socket says the list changed: added to a channel (or a new
  // DM), one deleted, or a message in a channel not listed yet.
  useEffect(() => {
    if (!isOpen) return undefined;
    return onMessagingEvent((event) => {
      if (event.event === "channels_changed") reloadChannels();
      else if (event.event === "activity" && !(channelsRef.current.mine || []).some((c) => c.id === event.channel_id)) {
        reloadChannels();
      }
    });
  }, [isOpen, reloadChannels]);

  // Live updates: one websocket per active channel, only while the modal is
  // open (it stays mounted while closed). New messages are appended; they're
  // marked read only while the Chat tab is showing them. The socket signs in
  // with its first message once it opens (the token never goes in the URL);
  // the server delivers nothing to it before that, and closes it for someone
  // who isn't in the channel. Once it is ready the channel is refetched, so a
  // message posted between the first fetch (or a dropped socket) and then is
  // not missed. How each close is answered: SOCKET_RETRY_CODES.
  useEffect(() => {
    if (!isOpen || !activeChannel) return undefined;
    const channel = activeChannel;
    let socket = null;
    let stopped = false;
    let retryTimer = null;
    let stableTimer = null;
    let failures = 0;
    let refreshToken = false;
    setLiveFeed("live");

    const connect = () => {
      if (stopped) return;
      let next;
      try {
        next = new WebSocket(messagingSocketUrl(channel.id));
      } catch (err) {
        console.warn("Messaging: websocket connection failed", err);
        setLiveFeed("stopped");
        return;
      }
      socket = next;
      wsRef.current = next;
      next.onopen = () => {
        authenticateSocket(next, { refresh: refreshToken }).catch((err) =>
          console.warn("Messaging: websocket sign-in failed", err?.message),
        );
      };
      next.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.event === "ready") {
            refreshToken = false;
            clearTimeout(stableTimer);
            stableTimer = setTimeout(() => {
              failures = 0;
            }, SOCKET_STABLE_MS);
            refreshShownChannelRef.current(channel);
          } else if (payload.event === "new_message") {
            setMessages((prev) => (prev.some((m) => m.id === payload.message.id) ? prev : [...prev, payload.message]));
            if (payload.message.sender_id !== currentUser.id) {
              // Read only if the thread is on screen; on the Tasks tab, or
              // with the browser tab hidden, it waits until it is shown.
              if (tabIndexRef.current === 0 && tabVisible()) {
                markChannelRead(channel.id, currentUser.id)
                  .then(() => markChannelReadLocally(channel.id))
                  .catch(() => {});
              } else {
                staleChannelRef.current = true;
              }
            }
          } else if (payload.event === "read") {
            // Someone else read this thread: add them to its "Seen by ...".
            if (payload.user_id !== currentUser.id) setReadBy((prev) => mergeReadBy(prev, payload));
          } else if (payload.event === "channel_deleted") {
            setActiveChannel(null);
            setMessages([]);
            reloadChannels();
            toast({ title: "This channel was deleted", status: "info", duration: 3000 });
          }
        } catch (err) {
          console.warn("Messaging: bad websocket payload", err);
        }
      };
      next.onclose = (event) => {
        if (stopped || socket !== next) return;
        clearTimeout(stableTimer);
        const code = event?.code;
        if (code === SOCKET_REMOVED) {
          // Taken out of the channel: drop the thread, as for a deleted channel.
          setActiveChannel(null);
          setMessages([]);
          reloadChannels();
          toast({ title: "You're no longer in this channel", status: "info", duration: 3000 });
          return;
        }
        if (!SOCKET_RETRY_CODES.includes(code) || failures >= SOCKET_MAX_RETRIES) {
          setLiveFeed("stopped");
          return;
        }
        refreshToken = code === 4401;
        const backoff = Math.min(SOCKET_RETRY_MAX_MS, SOCKET_RETRY_BASE_MS * 2 ** failures);
        const delay = Math.round(backoff * (1 + SOCKET_JITTER * (2 * Math.random() - 1)));
        failures += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    reconnectRef.current = () => {
      if (stopped) return;
      clearTimeout(retryTimer);
      failures = 0;
      setLiveFeed("live");
      connect();
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      clearTimeout(stableTimer);
      reconnectRef.current = null;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.close();
      }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeChannel?.id, currentUser?.id]);

  // A post in an agent's own thread is the poster's message to the agent too
  // (Plan 4 Task 9), and carries a client_key: the server answers the same key
  // again (per agent and author, for ten minutes) with the post it made. So the
  // words of the last post there that didn't go keep their key, and sent again
  // they reuse it: a post whose answer was lost is never made twice. Other
  // words are another post, with a key of their own. Like the draft, it belongs
  // to one conversation and is dropped when the reader moves to another.
  const unsentRef = useRef(null); // { channelId, body, format, key }
  useEffect(() => {
    unsentRef.current = null;
  }, [activeChannel?.id]);
  const clientKeyFor = (channelId, body, format) => {
    const last = unsentRef.current;
    if (last && last.channelId === channelId && last.body === body && last.format === format) return last.key;
    const key = newClientKey();
    unsentRef.current = { channelId, body, format, key };
    return key;
  };

  // Returns true when the message went out, false otherwise; the composer
  // keeps its text and files on false so the user can retry.
  // `mentions` ({user_ids, here, channel}), optional: who the composer's @list named.
  const handleSend = async (body, format = "text", files = [], mentions = null) => {
    if (!activeChannel) return false;
    const channelId = activeChannel.id;
    const channelName = activeChannel.name;
    const clientKey = activeChannel.agent_id ? clientKeyFor(channelId, body, format) : null;
    try {
      const attachments = files.length ? await uploadChatFiles(channelId, currentUser.id, files) : [];
      const answer = await sendMessage(channelId, currentUser.id, body, {
        format,
        attachments,
        ...(clientKey ? { clientKey } : {}),
        ...(mentions ? { mentions } : {}),
      });
      const { message } = answer;
      if (clientKey && unsentRef.current?.key === clientKey) unsentRef.current = null;
      const here = channelId === activeChannelIdRef.current;
      // The agent turned it down (it is paused): said at once, in its own
      // words. The server posts them in the thread too, but the live feed may
      // have stopped, and the reader may have moved on (then the thread is named).
      const refused = answer.agent?.refused;
      if (refused) {
        toast({ title: refused, description: here ? undefined : channelName, status: "warning", duration: 6000 });
      }
      if (!message) {
        // 202: the agent took the message, and its copy comes into the thread
        // (over the live feed) on the server's next pass.
        if (here && !refused) {
          // With the live feed stopped, it shows once the feed is back.
          const title =
            liveFeedRef.current === "stopped" ? "Sent. It will appear here." : "Sent. It will appear here in a moment.";
          toast({ title, status: "info", duration: 4000 });
        }
        return true;
      }
      // Sent either way (to the channel it was written in); only shown if
      // that channel is still the one open.
      if (message.channel_id === activeChannelIdRef.current) {
        setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      }
      return true;
    } catch (err) {
      toast({
        title: files.length ? "Couldn't send attachments" : "Message failed to send",
        description: err.message,
        status: "error",
        duration: 5000,
      });
      return false;
    }
  };

  // A channel's bell: its notify level or mute, saved for me. Shown at once from
  // the answer; the server's `read` event refreshes the other tabs.
  const handleChannelNotify = async (channel, patch) => {
    try {
      const saved = await setChannelNotify(channel.id, patch);
      applyChannelSettingsLocally(channel.id, saved || patch);
    } catch (err) {
      // The server's words, e.g. the database still needing the 2026-11-03 migration.
      toast({ title: "Couldn't change this channel's notifications", description: err.message, status: "error", duration: 6000 });
    }
  };

  const handleJoin = async (channel) => {
    try {
      await joinChannel(channel.id, currentUser.id);
      await reloadChannels();
      selectChannel(channel);
    } catch (err) {
      toast({ title: "Couldn't join channel", description: err.message, status: "error", duration: 4000 });
    }
  };

  const handleInvite = async (user) => {
    if (!activeChannel) return;
    try {
      await inviteToChannel(activeChannel.id, currentUser.id, user.id);
      toast({ title: `${user.name || user.email} added`, status: "success", duration: 2500 });
    } catch (err) {
      toast({ title: "Couldn't invite that person", description: err.message, status: "error", duration: 4000 });
    }
  };

  const handleStartDm = async (otherUser) => {
    try {
      const { channel } = await getOrCreateDm(currentUser.id, otherUser.id);
      await reloadChannels();
      selectChannel(channel);
    } catch (err) {
      toast({ title: "Couldn't start conversation", description: err.message, status: "error", duration: 4000 });
    }
  };

  const isDeleteConfirmed =
    !!channelPendingDelete &&
    deleteEmailConfirm.trim().toLowerCase() === (currentUser?.email || "").toLowerCase();

  const handleConfirmDelete = async () => {
    if (!channelPendingDelete || !isDeleteConfirmed) return;
    try {
      await deleteChannel(channelPendingDelete.id, currentUser.id);
      if (activeChannel?.id === channelPendingDelete.id) {
        setActiveChannel(null);
        setMessages([]);
      }
      await reloadChannels();
      toast({ title: `#${channelPendingDelete.name} deleted`, status: "info", duration: 2500 });
    } catch (err) {
      toast({ title: "Couldn't delete channel", description: err.message, status: "error", duration: 4000 });
    } finally {
      setChannelPendingDelete(null);
    }
  };

  return (
    <>
      {/* Chakra only scroll-locks the FIRST open modal, and that lock swallows
          wheel events inside any modal stacked on top of it (the Assign window,
          Create channel). Release it while one of those is open. */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" blockScrollOnMount={!assignTarget && !showCreateChannel}>
        <ModalOverlay />
        <ModalContent h="80vh" containerProps={{ onDragOver: preventStrayFileDrop, onDrop: preventStrayFileDrop }}>
          {/* Each panel mounts the first time it's shown, then stays (the Tasks
              panel keeps its picks; a hidden one doesn't duplicate the chat's text). */}
          <Tabs
            index={tabIndex}
            onChange={setTabIndex}
            isLazy
            lazyBehavior="keepMounted"
            display="flex"
            flexDirection="column"
            h="100%"
            variant="line"
            colorScheme="blue"
          >
            <ModalHeader pb={0}>
              <HStack spacing={2} pr={8}>
                <Text as="span" flex="1">
                  Messages
                </Text>
                <NotificationSettings />
              </HStack>
              <TabList mt={2} borderBottomWidth="1px">
                <Tab fontSize="sm">Chat</Tab>
                <Tab fontSize="sm">
                  Tasks
                  {tasksBadgeCount > 0 && (
                    <Badge ml={2} colorScheme="red" borderRadius="full">
                      {tasksBadgeCount}
                    </Badge>
                  )}
                </Tab>
              </TabList>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={0} display="flex" overflow="hidden" flex="1" minH={0}>
              <TabPanels h="100%" w="100%">
                <TabPanel p={0} h="100%" display="flex" flexDirection="column">
                  <DesktopNotificationsPrompt />
                  <Flex w="100%" flex="1" minH={0}>
                    <ChannelList
                      channels={channels}
                      activeChannelId={activeChannel?.id}
                      unreadCounts={summary.counts}
                      mentionCounts={summary.mentions}
                      channelSettings={summary.settings}
                      presence={presence}
                      canManageChannels={canManageChannels}
                      schema={currentUser?.schema}
                      onSelect={selectChannel}
                      onJoin={handleJoin}
                      onDelete={setChannelPendingDelete}
                      onRequestCreateChannel={() => setShowCreateChannel(true)}
                      onStartDm={handleStartDm}
                    />
                    <ChannelThread
                      channel={activeChannel}
                      messages={messages}
                      readBy={readBy}
                      currentUser={currentUser}
                      schema={currentUser?.schema}
                      linkableItems={myLinkableItems}
                      onSend={handleSend}
                      onRequestAssign={(message) => setAssignTarget({ message })}
                      onInvite={handleInvite}
                      notifySettings={activeChannel ? summary.settings[activeChannel.id] || null : null}
                      onChangeNotify={activeChannel ? (patch) => handleChannelNotify(activeChannel, patch) : null}
                      notice={
                        liveFeed === "stopped" ? (
                          <HStack role="status" px={4} py={2} bg="orange.50" borderBottom="1px solid" borderColor="orange.200">
                            <Text fontSize="sm" color="orange.800" flex="1">
                              Live updates stopped.
                            </Text>
                            <Button size="xs" onClick={() => reconnectRef.current?.()}>
                              Reconnect
                            </Button>
                          </HStack>
                        ) : null
                      }
                    />
                  </Flex>
                </TabPanel>
                <TabPanel p={0} h="100%">
                  <TasksPanel
                    items={taskItems}
                    channels={channels.mine}
                    loading={tasksLoading}
                    currentUser={currentUser}
                    onItemChanged={handleTaskItemChanged}
                    onReload={reloadTasks}
                    onNewTask={() => setAssignTarget({ defaultTab: 0 })}
                    onAssignOrder={() => setAssignTarget({ defaultTab: 1 })}
                    focus={taskFocus}
                    onFocusHandled={() => setTaskFocus(null)}
                  />
                </TabPanel>
              </TabPanels>
            </ModalBody>
          </Tabs>
        </ModalContent>
      </Modal>

      <CreateChannelModal
        isOpen={showCreateChannel}
        onClose={() => setShowCreateChannel(false)}
        currentUser={currentUser}
        onCreated={() => reloadChannels()}
      />

      {assignTarget && (
        <AssignModal
          isOpen
          onClose={() => setAssignTarget(null)}
          currentUser={currentUser}
          sourceChannelId={assignTarget.message ? activeChannel?.id : null}
          sourceMessage={assignTarget.message}
          defaultTab={assignTarget.defaultTab || 0}
          onAssigned={reloadTasks}
        />
      )}

      <AlertDialog isOpen={!!channelPendingDelete} onClose={() => setChannelPendingDelete(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete #{channelPendingDelete?.name}?</AlertDialogHeader>
            <AlertDialogBody>
              <Box mb={3}>
                This permanently deletes the channel and all its messages for everyone. This can't be undone.
              </Box>
              <FormControl>
                <FormLabel>Type your account email ({currentUser?.email}) to confirm</FormLabel>
                <Input
                  value={deleteEmailConfirm}
                  onChange={(e) => setDeleteEmailConfirm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && isDeleteConfirmed && handleConfirmDelete()}
                  placeholder={currentUser?.email}
                  autoFocus
                />
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={() => setChannelPendingDelete(null)} mr={3}>
                Cancel
              </Button>
              <Button colorScheme="red" isDisabled={!isDeleteConfirmed} onClick={handleConfirmDelete}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
